import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AdminCommandMenu } from "./AdminCommandMenu";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  FolderTree,
  Tags,
  Percent,
  Image,
  FileText,
  Settings,
  LogOut,
  ChevronRight,
  Boxes,
  Menu,
  X,
  Mail,
  MessageSquare,
  PenLine,
  Quote,
  RotateCcw,
  Gift,
  BarChart3,
  Layers,
  Sparkles,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navItems = [
  { title: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { title: "Analytics", href: "/admin/analytics", icon: BarChart3 },
  { title: "Products", href: "/admin/products", icon: Package },
  { title: "Variants", href: "/admin/variants", icon: Layers },
  { title: "Artisans", href: "/admin/artisans", icon: Users },
  { title: "Inventory", href: "/admin/inventory", icon: Boxes },
  { title: "Orders", href: "/admin/orders", icon: ShoppingCart },
  { title: "Returns", href: "/admin/returns", icon: RotateCcw },
  { title: "Customers", href: "/admin/customers", icon: Users },
  { title: "Segments", href: "/admin/segments", icon: Users },
  { title: "Wholesale", href: "/admin/wholesale", icon: Boxes },
  { title: "Support Tickets", href: "/admin/tickets", icon: MessageSquare },
  { title: "WhatsApp Broadcasts", href: "/admin/whatsapp", icon: MessageSquare },
  { title: "Gift Cards", href: "/admin/gift-cards", icon: Gift },
  { title: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { title: "Testimonials", href: "/admin/testimonials", icon: Quote },
  { title: "Newsletter", href: "/admin/newsletter", icon: Mail },
  { title: "Email Template", href: "/admin/email-template", icon: PenLine },
  { title: "Categories", href: "/admin/categories", icon: FolderTree },
  { title: "Brands", href: "/admin/brands", icon: Tags },
  { title: "Discounts", href: "/admin/discounts", icon: Percent },
  { title: "Banners", href: "/admin/banners", icon: Image },
  { title: "Site Content", href: "/admin/site-content", icon: FileText },
  { title: "CMS Pages", href: "/admin/pages", icon: FileText },
  { title: "Marketing AI", href: "/admin/marketing", icon: Sparkles },
  { title: "Forecasting", href: "/admin/forecasting", icon: Sparkles },
  { title: "AI Insights", href: "/admin/inventory-insights", icon: BarChart3 },
  { title: "Re-engagement", href: "/admin/reengagement", icon: Users },
  { title: "Audit Logs", href: "/admin/audit-logs", icon: FileText },
  { title: "Settings", href: "/admin/settings", icon: Settings },
];

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const { signOut } = useAuth();
  const location = useLocation();

  return (
    <>
      <div className="p-6">
        <Link to="/admin" className="flex items-center gap-2" onClick={onNavClick}>
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Package className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-display text-xl font-semibold text-foreground">
            Gaonova
          </span>
        </Link>
        <p className="text-xs text-muted-foreground mt-1">Admin Dashboard</p>
      </div>

      <Separator />

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavClick}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.title}
                {isActive && <ChevronRight className="ml-auto h-4 w-4" />}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      <Separator />

      <div className="p-4 space-y-2">
        <Link to="/" onClick={onNavClick}>
          <Button variant="outline" className="w-full justify-start" size="sm">
            <Package className="mr-2 h-4 w-4" />
            View Store
          </Button>
        </Link>
        <Button
          variant="ghost"
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
          size="sm"
          onClick={() => signOut()}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </>
  );
}

export function AdminLayout() {
  const { user, isAdmin, loading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Defense-in-depth: If not loading and no admin role, force exit immediately
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/auth", { replace: true });
      } else if (!isAdmin) {
        console.error("Security Alert: Non-admin attempted to access /admin");
        signOut().then(() => navigate("/auth", { replace: true }));
      }
    }
  }, [user, isAdmin, loading, navigate, signOut]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading || !user || !isAdmin) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm font-medium text-muted-foreground animate-pulse">Verifying Security Credentials...</p>
        </div>
      </div>
    );
  }
  return (
    <div dir="ltr" className="flex h-screen bg-background">
      {/* Admin chrome stays LTR even under Arabic locale (see RTL block in src/index.css). */}
      {/* Global Command Header */}
      <header className="fixed top-0 left-0 right-0 h-16 md:h-20 bg-background/80 backdrop-blur-md border-b border-border/50 z-40 transition-all duration-300 md:left-64">
        <div className="flex h-full items-center justify-between px-4 md:px-8 gap-4">
          
          {/* Left: Mobile Toggle & Context (Hidden on Desktop) */}
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary transition-colors">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 flex flex-col border-r border-border">
                <SidebarContent onNavClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="font-display font-bold text-foreground truncate max-w-[120px]">Admin</span>
          </div>

          {/* Center: The Command Bar (Focal Point) */}
          <div className="flex-1 max-w-2xl mx-auto flex justify-center">
            <AdminCommandMenu />
          </div>

          {/* Right: Pulse Widgets (Live Context) */}
          <div className="flex items-center gap-3">
            {/* Live Orders Pulse */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-forest/[0.03] border border-forest/10 rounded-full group cursor-help transition-all hover:bg-forest/5">
              <div className="h-2 w-2 rounded-full bg-forest animate-pulse" />
              <span className="text-[10px] font-bold text-forest/70 uppercase tracking-wider">Live Orders</span>
            </div>
            
            {/* System Health */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/[0.03] border border-primary/10 rounded-full hover:bg-primary/5 transition-all">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="hidden sm:inline text-[10px] font-bold text-primary/70 uppercase tracking-wider">Secure</span>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-border bg-card flex-col fixed inset-y-0 z-50">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 md:pt-20 md:ml-64 bg-background/50">
        <div className="container mx-auto p-4 md:p-8 animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
